import React, { useState, cloneElement } from 'react';
import { SectionErrorBoundary } from '../components/SectionErrorBoundary';
import { Users, ShoppingBag, DollarSign, BarChart3, Settings, Star, Zap, MessageSquare, Crown, CalendarDays, Layers, TrendingUp, LogOut, FileText, LayoutGrid, Target, Megaphone, Store, LayoutDashboard, Video, Calendar, CreditCard, Menu, X, Package, ChevronRight } from 'lucide-react';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { useUser } from '../contexts/UserContext';
import { useLanguage } from '../i18n/LanguageContext';
// Import all tabs (existing and new)
import { OverviewTab } from '../components/astrologer-dashboard/OverviewTab';
import { OrdersTab } from '../components/astrologer-dashboard/OrdersTab';
import { ConsultationsTab } from '../components/astrologer-dashboard/ConsultationsTab';
import { ChatTab } from '../components/astrologer-dashboard/ChatTab';
import { ServicesTab } from '../components/astrologer-dashboard/ServicesTab';
import { MarketplaceTab } from '../components/astrologer-dashboard/MarketplaceTab';
import { CalendarScheduleTab } from '../components/astrologer-dashboard/CalendarScheduleTab';
import { FinancesTab } from '../components/astrologer-dashboard/FinancesTab';
import { PromotionTab } from '../components/astrologer-dashboard/PromotionTab';
import { ReviewsTab } from '../components/astrologer-dashboard/ReviewsTab';
import { StatsTab } from '../components/astrologer-dashboard/StatsTab';
import { ProfileTab } from '../components/astrologer-dashboard/ProfileTab';
import { SettingsSecurityTab } from '../components/astrologer-dashboard/SettingsSecurityTab';
import { SubscriptionTab } from '../components/astrologer-dashboard/SubscriptionTab';
import { PackagesTab } from '../components/astrologer-dashboard/PackagesTab';
import { AutoServiceDetail } from '../components/astrologer-dashboard/AutoServiceDetail';
interface AstrologerDashboardPageProps {
  onNavigate: (page: string) => void;
}
export function AstrologerDashboardPage({
  onNavigate
}: AstrologerDashboardPageProps) {
  const {
    user,
    logout
  } = useUser();
  const {
    t
  } = useLanguage();
  const [activeTab, setActiveTab] = useState('overview');
  const [activeAutoService, setActiveAutoService] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 2500);
  };
  // 16-section sidebar grouped logically
  const menuGroups = [{
    title: t('astrodash.group.main'),
    items: [{
      id: 'overview',
      label: t('astrodash.tab.overview'),
      icon: <LayoutDashboard className="w-5 h-5" data-id="element-20931" />
    }, {
      id: 'orders',
      label: t('astrodash.tab.orders'),
      icon: <ShoppingBag className="w-5 h-5" data-id="element-20932" />
    }, {
      id: 'consultations',
      label: t('astrodash.tab.consultations'),
      icon: <Video className="w-5 h-5" data-id="element-20933" />
    }, {
      id: 'chat',
      label: t('astrodash.tab.chat'),
      icon: <MessageSquare className="w-5 h-5" data-id="element-20934" />
    }]
  }, {
    title: t('astrodash.group.products'),
    items: [{
      id: 'services',
      label: t('astrodash.tab.services'),
      icon: <Layers className="w-5 h-5" data-id="element-20935" />
    }, {
      id: 'marketplace',
      label: t('astrodash.tab.marketplace'),
      icon: <Store className="w-5 h-5" data-id="element-20936" />
    }, {
      id: 'calendar',
      label: t('astrodash.tab.calendar'),
      icon: <Calendar className="w-5 h-5" data-id="element-20937" />
    }]
  }, {
    title: t('astrodash.group.business'),
    items: [{
      id: 'finances',
      label: t('astrodash.tab.finances'),
      icon: <DollarSign className="w-5 h-5" data-id="element-20938" />
    }, {
      id: 'promotion',
      label: t('astrodash.tab.promotion'),
      icon: <Zap className="w-5 h-5" data-id="element-20939" />
    }, {
      id: 'reviews',
      label: t('astrodash.tab.reviews'),
      icon: <Star className="w-5 h-5" data-id="element-20940" />
    }, {
      id: 'stats',
      label: t('astrodash.tab.stats'),
      icon: <BarChart3 className="w-5 h-5" data-id="element-20941" />
    }]
  }, {
    title: t('astrodash.group.settings'),
    items: [{
      id: 'profile',
      label: t('astrodash.tab.profile'),
      icon: <Users className="w-5 h-5" data-id="element-20942" />
    }, {
      id: 'settings',
      label: t('astrodash.tab.settings'),
      icon: <Settings className="w-5 h-5" data-id="element-20943" />
    }, {
      id: 'subscription',
      label: t('astrodash.tab.subscription'),
      icon: <CreditCard className="w-5 h-5" data-id="element-20944" />
    }]
  }];
  const handleTabChange = (id: string) => {
    setActiveTab(id);
    setActiveAutoService(null);
    setMobileMenuOpen(false);
  };
  const renderContent = () => {
    if (activeAutoService) {
      return <AutoServiceDetail activeAutoService={activeAutoService} setActiveAutoService={setActiveAutoService} showToast={showToast} data-id="element-20945" />;
    }
    switch (activeTab) {
      case 'overview':
        return <OverviewTab showToast={showToast} setActiveTab={setActiveTab} data-id="element-20946" />;
      case 'orders':
        return <OrdersTab showToast={showToast} setActiveTab={setActiveTab} data-id="element-20947" />;
      case 'consultations':
        return <ConsultationsTab showToast={showToast} setActiveTab={setActiveTab} data-id="element-20948" />;
      case 'chat':
        return <ChatTab showToast={showToast} setActiveTab={setActiveTab} data-id="element-20949" />;
      case 'services':
        return <ServicesTab showToast={showToast} setActiveTab={setActiveTab} data-id="element-20950" />;
      case 'marketplace':
        return <MarketplaceTab showToast={showToast} setActiveTab={setActiveTab} data-id="element-20951" />;
      case 'calendar':
        return <CalendarScheduleTab showToast={showToast} data-id="element-20952" />;
      case 'finances':
        return <FinancesTab showToast={showToast} setActiveTab={setActiveTab} data-id="element-20953" />;
      case 'promotion':
        return <PromotionTab showToast={showToast} setActiveAutoService={setActiveAutoService} setActiveTab={setActiveTab} data-id="element-20954" />;
      case 'reviews':
        return <ReviewsTab showToast={showToast} setActiveTab={setActiveTab} data-id="element-20955" />;
      case 'stats':
        return <StatsTab showToast={showToast} setActiveTab={setActiveTab} data-id="element-20956" />;
      case 'profile':
        return <ProfileTab showToast={showToast} onNavigate={onNavigate} setActiveTab={handleTabChange} data-id="element-20957" />;
      case 'settings':
        return <SettingsSecurityTab showToast={showToast} initialTab="general" data-id="element-20958" />;
      case 'subscription':
        return <SubscriptionTab showToast={showToast} data-id="element-20959" />;
      default:
        return <div className="flex flex-col items-center justify-center py-20 text-slate-500" data-id="element-20960">
            <Settings className="w-12 h-12 text-slate-300 mb-3" data-id="element-20961" />
            <p className="text-lg font-medium text-slate-900 mb-1" data-id="element-20962">
              {t('astrodash.common.noData')}
            </p>
            <p className="text-sm mb-4" data-id="element-20963">{t('astrodash.tab.overview')}</p>
            <Button variant="primary" size="sm" onClick={() => setActiveTab('overview')} data-id="element-20964">
              {t('astrodash.common.back')}
            </Button>
          </div>;
    }
  };
  // Get current tab label for mobile header
  const currentTabLabel = menuGroups.flatMap(g => g.items).find(item => item.id === activeTab)?.label || t('astrodash.tab.overview');
  const currentTabIcon = menuGroups.flatMap(g => g.items).find(item => item.id === activeTab)?.icon || <LayoutDashboard className="w-5 h-5" data-id="element-20965" />;
  // Bottom nav items (5 most used)
  const bottomNavItems = [{
    id: 'overview',
    label: 'Обзор',
    icon: <LayoutDashboard className="w-5 h-5" data-id="element-20966" />
  }, {
    id: 'orders',
    label: 'Заказы',
    icon: <ShoppingBag className="w-5 h-5" data-id="element-20967" />
  }, {
    id: 'chat',
    label: 'Чат',
    icon: <MessageSquare className="w-5 h-5" data-id="element-20968" />
  }, {
    id: 'finances',
    label: 'Финансы',
    icon: <DollarSign className="w-5 h-5" data-id="element-20969" />
  }, {
    id: 'stats',
    label: 'Статистика',
    icon: <BarChart3 className="w-5 h-5" data-id="element-20970" />
  }];
  return <div className="w-full bg-slate-50 min-h-screen pb-6 lg:pb-24" data-id="element-20971">
      {/* Mobile Bottom Nav Spacer */}
      <div className="h-0 lg:hidden" style={{
      paddingBottom: 'env(safe-area-inset-bottom, 0px)'
    }} data-id="element-20972" />

      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 pt-4 lg:pt-12" data-id="element-20973">
        {/* Mobile Header */}
        <div className="lg:hidden flex items-center justify-between bg-white p-3 rounded-2xl border border-slate-200 shadow-sm mb-4" data-id="element-20974">
          <div className="flex items-center gap-3 min-w-0" data-id="element-20975">
            <div className="w-9 h-9 rounded-xl bg-indigo-100 flex items-center justify-center text-xs font-bold text-indigo-700 uppercase flex-shrink-0" data-id="element-20976">
              {user?.name?.slice(0, 2) || 'АС'}
            </div>
            <div className="min-w-0" data-id="element-20977">
              <div className="font-bold text-slate-900 text-sm truncate" data-id="element-20978">
                {currentTabLabel}
              </div>
              <div className="flex items-center gap-1.5" data-id="element-20979">
                <Badge variant="pro" size="sm" data-id="element-20980">
                  <Crown className="w-3 h-3 mr-0.5" data-id="element-20981" /> PRO
                </Badge>
              </div>
            </div>
          </div>
          <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="p-2 rounded-xl hover:bg-slate-100 text-slate-600 transition-colors flex-shrink-0" data-id="element-20982">
            <Menu className="w-5 h-5" data-id="element-20983" />
          </button>
        </div>

        {/* Mobile Sidebar Overlay */}
        {mobileMenuOpen && <div className="fixed inset-0 z-50 lg:hidden" data-id="element-20984">
            {/* Backdrop */}
            <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setMobileMenuOpen(false)} data-id="element-20985" />
            {/* Sidebar Panel */}
            <div className="absolute right-0 top-0 bottom-0 w-[85%] max-w-sm bg-white shadow-2xl overflow-hidden animate-in slide-in-from-right duration-200 flex flex-col" data-id="element-20986">
              {/* Mobile Menu Header */}
              <div className="p-4 border-b border-slate-100 bg-slate-50/50 flex items-center justify-between flex-shrink-0" data-id="element-20987">
                <div className="flex items-center gap-3" data-id="element-20988">
                  <div className="w-10 h-10 rounded-xl bg-indigo-100 flex items-center justify-center text-sm font-bold text-indigo-700 uppercase" data-id="element-20989">
                    {user?.name?.slice(0, 2) || 'АС'}
                  </div>
                  <div data-id="element-20990">
                    <div className="font-bold text-slate-900 text-sm" data-id="element-20991">
                      {user?.name || t('astrodash.role')}
                    </div>
                    <div className="flex items-center gap-1.5 mt-0.5" data-id="element-20992">
                      <Badge variant="pro" size="sm" data-id="element-20993">
                        <Crown className="w-3 h-3 mr-0.5" data-id="element-20994" /> PRO{' '}
                        {t('astrodash.promo.tariff')}
                      </Badge>
                    </div>
                  </div>
                </div>
                <button onClick={() => setMobileMenuOpen(false)} className="p-2 rounded-xl hover:bg-slate-100 text-slate-400 transition-colors" data-id="element-20995">
                  <X className="w-5 h-5" data-id="element-20996" />
                </button>
              </div>

              {/* Navigation */}
              <nav className="flex-1 overflow-y-auto p-3" data-id="element-20997">
                {menuGroups.map((group, idx) => <div key={idx} className="mb-4 last:mb-0" data-id="element-20998">
                    <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2 px-3" data-id="element-20999">
                      {group.title}
                    </div>
                    {group.items.map(item => <button key={item.id} onClick={() => handleTabChange(item.id)} className={`w-full flex items-center justify-between px-3 py-2.5 mb-0.5 rounded-xl text-sm font-medium transition-colors ${activeTab === item.id && !activeAutoService ? 'bg-indigo-50 text-indigo-700' : 'text-slate-600 hover:bg-slate-50 active:bg-slate-100'}`} data-id="element-21000">
                        <div className="flex items-center" data-id="element-21001">
                          <span className={`mr-3 shrink-0 ${activeTab === item.id && !activeAutoService ? 'text-indigo-600' : 'text-slate-400'}`} data-id="element-21002">
                            {item.icon}
                          </span>
                          <span data-id="element-21003">{item.label}</span>
                        </div>
                        {activeTab === item.id && !activeAutoService && <ChevronRight className="w-4 h-4 text-indigo-400" data-id="element-21004" />}
                      </button>)}
                  </div>)}
              </nav>

              {/* Logout */}
              <div className="p-3 border-t border-slate-100 flex-shrink-0" data-id="element-21005">
                <button onClick={() => {
              logout();
              onNavigate('home');
            }} className="w-full flex items-center px-3 py-2.5 rounded-xl text-sm font-medium text-rose-600 hover:bg-rose-50 active:bg-rose-100 transition-colors" data-id="element-21006">
                  <LogOut className="w-4 h-4 mr-3 text-rose-400" data-id="element-21007" />{' '}
                  {t('astrodash.logout')}
                </button>
              </div>
            </div>
          </div>}

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8" data-id="element-21008">
          {/* Desktop Sidebar */}
          <div className="hidden lg:block lg:col-span-3" data-id="element-21009">
            <Card padding="none" className="sticky top-24 overflow-hidden border-slate-200 shadow-sm" data-id="element-21010">
              {/* Desktop User Info */}
              <div className="p-6 border-b border-slate-100 bg-slate-50/50" data-id="element-21011">
                <div className="flex flex-col items-center text-center" data-id="element-21012">
                  <div className="w-16 h-16 rounded-2xl bg-indigo-100 flex items-center justify-center text-2xl font-bold text-indigo-700 mb-3 uppercase" data-id="element-21013">
                    {user?.name?.slice(0, 2) || 'АС'}
                  </div>
                  <div className="font-bold text-slate-900 text-lg mb-2" data-id="element-21014">
                    {user?.name || t('astrodash.role')}
                  </div>
                  <div className="w-full bg-indigo-50 border border-indigo-100 rounded-xl p-3 flex flex-col items-center gap-2" data-id="element-21015">
                    <div className="flex items-center text-indigo-700 font-medium text-sm" data-id="element-21016">
                      <Crown className="w-4 h-4 mr-1.5" data-id="element-21017" /> PRO{' '}
                      {t('astrodash.promo.tariff')}
                    </div>
                    <Button variant="outline" size="sm" fullWidth className="text-xs border-indigo-200 text-indigo-700 hover:bg-indigo-100/50 bg-white" onClick={() => handleTabChange('subscription')} data-id="element-21018">
                      {t('astrodash.tab.subscription')}
                    </Button>
                  </div>
                </div>
              </div>

              {/* Desktop Navigation */}
              <nav className="p-3 h-[calc(100vh-300px)] overflow-y-auto custom-scrollbar" data-id="element-21019">
                {menuGroups.map((group, idx) => <div key={idx} className="mb-4 last:mb-0" data-id="element-21020">
                    <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2 px-3" data-id="element-21021">
                      {group.title}
                    </div>
                    {group.items.map(item => <button key={item.id} onClick={() => handleTabChange(item.id)} title={item.label} className={`w-full flex items-center px-3 py-2 mb-0.5 rounded-lg text-sm font-medium transition-colors ${activeTab === item.id && !activeAutoService ? 'bg-indigo-50 text-indigo-700' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'}`} data-id="element-21022">
                        <span className={`mr-3 shrink-0 ${activeTab === item.id && !activeAutoService ? 'text-indigo-600' : 'text-slate-400'}`} data-id="element-21023">
                          {item.icon}
                        </span>
                        <span className="truncate" data-id="element-21024">{item.label}</span>
                      </button>)}
                  </div>)}

                <div className="pt-4 mt-2 border-t border-slate-100" data-id="element-21025">
                  <button onClick={() => {
                  logout();
                  onNavigate('home');
                }} className="w-full flex items-center px-3 py-2 rounded-lg text-sm font-medium text-rose-600 hover:bg-rose-50 transition-colors" data-id="element-21026">
                    <LogOut className="w-4 h-4 mr-3 text-rose-400" data-id="element-21027" />{' '}
                    {t('astrodash.logout')}
                  </button>
                </div>
              </nav>
            </Card>
          </div>

          {/* Main Content Area */}
          <div className="lg:col-span-9 pb-20 lg:pb-0" data-id="element-21028">
            <SectionErrorBoundary key={activeTab} sectionName={`astrologer-${activeTab}`} fallbackMinHeight="min-h-[400px]" data-id="element-21029">
              {renderContent()}
            </SectionErrorBoundary>
          </div>
        </div>
      </div>

      {/* Mobile Bottom Navigation */}
      <div className="fixed bottom-0 left-0 right-0 z-40 lg:hidden bg-white border-t border-slate-200 shadow-[0_-4px_20px_rgba(0,0,0,0.06)]" style={{
      paddingBottom: 'env(safe-area-inset-bottom, 0px)'
    }} data-id="element-21030">
        <div className="flex items-center justify-around px-1 py-1.5" data-id="element-21031">
          {bottomNavItems.map(item => {
          const isActive = activeTab === item.id && !activeAutoService;
          return <button key={item.id} onClick={() => handleTabChange(item.id)} className={`flex flex-col items-center justify-center py-1.5 px-2 rounded-xl min-w-[56px] transition-colors ${isActive ? 'text-indigo-600' : 'text-slate-400 active:text-slate-600'}`} data-id="element-21032">
                <span className={isActive ? 'text-indigo-600' : 'text-slate-400'} data-id="element-21033">
                  {cloneElement(item.icon, {
                className: 'w-5 h-5'
              })}
                </span>
                <span className={`text-[10px] mt-0.5 font-medium ${isActive ? 'text-indigo-600' : 'text-slate-500'}`} data-id="element-21034">
                  {item.label}
                </span>
                {isActive && <div className="w-4 h-0.5 bg-indigo-500 rounded-full mt-0.5" data-id="element-21035" />}
              </button>;
        })}
          <button onClick={() => setMobileMenuOpen(true)} className="flex flex-col items-center justify-center py-1.5 px-2 rounded-xl min-w-[56px] text-slate-400 active:text-slate-600 transition-colors" data-id="element-21036">
            <Menu className="w-5 h-5" data-id="element-21037" />
            <span className="text-[10px] mt-0.5 font-medium text-slate-500" data-id="element-21038">
              Ещё
            </span>
          </button>
        </div>
      </div>

      {/* Toast notification */}
      {toast && <div className="fixed bottom-20 lg:bottom-6 left-1/2 -translate-x-1/2 z-50 bg-slate-800 text-white text-sm font-medium px-4 py-2.5 rounded-xl shadow-lg animate-[fadeIn_0.2s_ease-out]" data-id="element-21039">
          {toast}
        </div>}
    </div>;
}